import { minBy } from 'es-toolkit';

import { CeilingTray } from '@paragon/contract-library';
import {
  angleEquals,
  angleLessThanOrEqual,
  centerPoint3D,
  convertDegreesToRadians,
  distanceLessThanOrEqual,
  Point2D,
  Point3D,
  Polygon3D,
} from '@paragon/geometry-library';
import { Point2DInterface } from '@paragon/geometry-library/source/scripts/Point2D';
import { circularPairwise } from '@paragon/utilities';

function getInsetDistance(trayHeight: number, angle: number) {
  return trayHeight / Math.tan(convertDegreesToRadians(angle));
}

function toPolygonIfValid(vertices: Point3D[]): Polygon3D | null {
  if (circularPairwise(vertices).some(([v1, v2]) => v1.equals(v2))) return null;

  return new Polygon3D(vertices);
}

export class SlopedBox {
  lowerLeftBottom: Point3D;
  lowerRightBottom: Point3D;
  upperLeftBottom: Point3D;
  upperRightBottom: Point3D;
  lowerLeftTop: Point3D;
  lowerRightTop: Point3D;
  upperLeftTop: Point3D;
  upperRightTop: Point3D;

  private constructor(
    lowerLeftBottom: Point3D,
    lowerRightBottom: Point3D,
    upperLeftBottom: Point3D,
    upperRightBottom: Point3D,
    lowerLeftTop: Point3D,
    lowerRightTop: Point3D,
    upperLeftTop: Point3D,
    upperRightTop: Point3D,
  ) {
    this.lowerLeftBottom = lowerLeftBottom;
    this.lowerRightBottom = lowerRightBottom;
    this.upperLeftBottom = upperLeftBottom;
    this.upperRightBottom = upperRightBottom;
    this.lowerLeftTop = lowerLeftTop;
    this.lowerRightTop = lowerRightTop;
    this.upperLeftTop = upperLeftTop;
    this.upperRightTop = upperRightTop;
  }

  static fromDescription(description: {
    position: Point2DInterface;
    bottomElevation: number;
    topElevation: number;
    width: number;
    length: number;
    verticalAnchor: CeilingTray['verticalAnchor'];
    horizontalAnchor: CeilingTray['horizontalAnchor'];
    rotationAngle: number;
    sideAngle: number;
  }): SlopedBox | null {
    const { position, bottomElevation, topElevation, width, length, rotationAngle, sideAngle } = description;
    const height = topElevation - bottomElevation;
    if (distanceLessThanOrEqual(height, 0)) return null;

    if (angleLessThanOrEqual(sideAngle, 0) || sideAngle > 90) return null;
    const insetDistance = angleEquals(sideAngle, 90) ? 0 : getInsetDistance(height, sideAngle);

    const lowerLeftBottom = new Point3D(0, 0, bottomElevation);
    const lowerRightBottom = new Point3D(width, 0, bottomElevation);
    const upperLeftBottom = new Point3D(0, length, bottomElevation);
    const upperRightBottom = new Point3D(width, length, bottomElevation);

    const lowerLeftTop = new Point3D(insetDistance, insetDistance, topElevation);
    const lowerRightTop = new Point3D(width - insetDistance, insetDistance, topElevation);
    const upperLeftTop = new Point3D(insetDistance, length - insetDistance, topElevation);
    const upperRightTop = new Point3D(width - insetDistance, length - insetDistance, topElevation);

    // Validate bottom vertices to prevent crash if width or length is malformed.
    const bottomVertexPairs = circularPairwise([lowerLeftBottom, lowerRightBottom, upperRightBottom, upperLeftBottom]);
    if (bottomVertexPairs.some(([v1, v2]) => v1.equals(v2))) {
      return null;
    }

    const vertices = new SlopedBox(
      lowerLeftBottom,
      lowerRightBottom,
      upperLeftBottom,
      upperRightBottom,
      lowerLeftTop,
      lowerRightTop,
      upperLeftTop,
      upperRightTop,
    );

    const anchor = vertices.getAnchorPoint(description.verticalAnchor, description.horizontalAnchor);
    const rotated = angleEquals(rotationAngle, 0)
      ? vertices
      : vertices.mapVertices((point) => point.rotate2D(rotationAngle, anchor.projectTo2D()));

    const desiredPosition = Point2D.fromObject(position).to3D(bottomElevation);
    return rotated.mapVertices((point) => point.translate(desiredPosition.subtract(anchor).toVector()));
  }

  private mapVertices(fn: (point: Point3D) => Point3D): SlopedBox {
    return new SlopedBox(
      fn(this.lowerLeftBottom),
      fn(this.lowerRightBottom),
      fn(this.upperLeftBottom),
      fn(this.upperRightBottom),
      fn(this.lowerLeftTop),
      fn(this.lowerRightTop),
      fn(this.upperLeftTop),
      fn(this.upperRightTop),
    );
  }

  private getAnchorPoint(
    verticalAnchor: CeilingTray['verticalAnchor'],
    horizontalAnchor: CeilingTray['horizontalAnchor'],
  ) {
    const anchorsAre = (vertical: CeilingTray['verticalAnchor'], horizontal: CeilingTray['horizontalAnchor']) =>
      horizontalAnchor === horizontal && verticalAnchor === vertical;

    switch (true) {
      case anchorsAre('Upper', 'Left'):
        return this.upperLeftBottom;

      case anchorsAre('Center', 'Left'):
        return centerPoint3D([this.upperLeftBottom, this.lowerLeftBottom]);

      case anchorsAre('Lower', 'Left'):
        return this.lowerLeftBottom;

      case anchorsAre('Upper', 'Center'):
        return centerPoint3D([this.upperLeftBottom, this.upperRightBottom]);

      case anchorsAre('Center', 'Center'):
        return centerPoint3D([
          this.upperLeftBottom,
          this.upperRightBottom,
          this.lowerLeftBottom,
          this.lowerRightBottom,
        ]);

      case anchorsAre('Lower', 'Center'):
        return centerPoint3D([this.lowerLeftBottom, this.lowerRightBottom]);

      case anchorsAre('Upper', 'Right'):
        return this.upperRightBottom;

      case anchorsAre('Center', 'Right'):
        return centerPoint3D([this.upperRightBottom, this.lowerRightBottom]);

      case anchorsAre('Lower', 'Right'):
        return this.lowerRightBottom;

      default:
        throw new Error(`Invalid anchor point configuration—${horizontalAnchor} x ${verticalAnchor}.`);
    }
  }

  get facesExcludingBottom() {
    const horizontalFace = toPolygonIfValid([
      this.upperLeftTop,
      this.upperRightTop,
      this.lowerRightTop,
      this.lowerLeftTop,
    ]);

    if (horizontalFace == null) return null;

    // Check that corresponding upper and lower vertices are close to ensure the top polygon has not been flipped once
    //   or twice.
    const closestVertex = minBy(horizontalFace.vertices, (vertex) => vertex.distanceToPoint(this.lowerLeftBottom))!;
    if (!closestVertex.equals(this.lowerLeftTop)) return null;

    const leftFace = toPolygonIfValid([
      this.upperLeftBottom,
      this.upperLeftTop,
      this.lowerLeftTop,
      this.lowerLeftBottom,
    ]);

    const rightFace = toPolygonIfValid([
      this.lowerRightTop,
      this.upperRightTop,
      this.upperRightBottom,
      this.lowerRightBottom,
    ]);

    const upperFace = toPolygonIfValid([
      this.upperLeftBottom,
      this.upperRightBottom,
      this.upperRightTop,
      this.upperLeftTop,
    ]);

    const lowerFace = toPolygonIfValid([
      this.lowerLeftTop,
      this.lowerRightTop,
      this.lowerRightBottom,
      this.lowerLeftBottom,
    ]);

    // If any of the side faces are null the box's geometry is invalid, so return null.
    if (!leftFace || !rightFace || !upperFace || !lowerFace) return null;

    return [horizontalFace, leftFace, rightFace, upperFace, lowerFace];
  }

  get bottomFace() {
    return toPolygonIfValid([this.upperLeftBottom, this.lowerLeftBottom, this.lowerRightTop, this.upperRightTop]);
  }
}
